import React, { useState, useEffect, useRef, useCallback } from 'react';
import classNames from 'classnames';
import { streamIntentCard } from '../../../util/api';

import css from './AiIntentCard.module.css';

/**
 * AiIntentCard — Slim AI shopping intelligence strip shown above filter pills.
 *
 * Redesigned to be a compact, non-intrusive strip (like eBay's "Related:" row).
 * Collapsed: single line with AI summary + price range. Expandable on click.
 * Streams a brief, grounded AI response referencing the actual listings.
 * Caches responses per keyword in session to avoid re-streaming on back/forward.
 *
 * @param {string} keywords - The current search query
 * @param {Array} listings - Listing objects from Redux state
 * @param {number} totalItems - Total result count
 * @param {boolean} searchInProgress - Whether Sharetribe is still loading
 */
const AiIntentCard = ({ keywords, listings, totalItems, searchInProgress }) => {
  const [content, setContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [error, setError] = useState(null);

  // Session cache: keyword → { content, suggestions }
  const cacheRef = useRef(new Map());
  const abortRef = useRef(null);
  const lastKeywordsRef = useRef(null);

  // Parse follow-up suggestions from AI response (lines starting with ">> ")
  const parseSuggestions = useCallback(text => {
    const lines = text.split('\n');
    const suggestions = [];
    const bodyLines = [];

    for (const line of lines) {
      if (line.trimStart().startsWith('>> ')) {
        suggestions.push(
          line
            .trimStart()
            .slice(3)
            .trim()
        );
      } else {
        bodyLines.push(line);
      }
    }

    return {
      body: bodyLines.join('\n').trim(),
      suggestions,
    };
  }, []);

  // Calculate price stats from listings (client-side, no AI needed)
  const priceStats = React.useMemo(() => {
    if (!listings || listings.length === 0) return null;

    const prices = listings.map(l => l.attributes?.price?.amount).filter(p => p != null && p > 0);

    if (prices.length === 0) return null;

    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const avg = Math.round(prices.reduce((a, b) => a + b, 0) / prices.length);

    return { min, max, avg, count: prices.length };
  }, [listings]);

  useEffect(() => {
    // Skip if no keywords, still loading, or keywords unchanged
    if (!keywords || keywords.trim().length === 0) {
      setContent('');
      setIsComplete(false);
      return;
    }

    // Don't trigger while Sharetribe is still fetching listings
    if (searchInProgress) return;

    // Don't re-stream if keywords haven't changed
    if (keywords === lastKeywordsRef.current) return;
    lastKeywordsRef.current = keywords;

    // Abort any in-flight stream
    if (abortRef.current) {
      abortRef.current();
      abortRef.current = null;
    }

    // Check cache first
    const cached = cacheRef.current.get(keywords);
    if (cached) {
      setContent(cached.content);
      setIsComplete(true);
      setIsStreaming(false);
      setError(null);
      setIsExpanded(false);
      return;
    }

    // Need at least 1 listing to ground the response
    if (!listings || listings.length === 0) return;

    // Start streaming
    setContent('');
    setIsStreaming(true);
    setIsComplete(false);
    setError(null);
    setIsExpanded(false);

    let accumulated = '';

    const abort = streamIntentCard(keywords, listings, {
      onContent: text => {
        accumulated += text;
        setContent(accumulated);
      },
      onDone: () => {
        setIsStreaming(false);
        setIsComplete(true);
        // Cache the response
        cacheRef.current.set(keywords, { content: accumulated });
      },
      onError: (msg, code) => {
        setIsStreaming(false);
        setError(msg);
        console.warn('[AiIntentCard] Stream error:', code, msg);
      },
    });

    abortRef.current = abort;

    return () => {
      if (abortRef.current) {
        abortRef.current();
        abortRef.current = null;
      }
    };
  }, [keywords, listings, searchInProgress]);

  // Nothing to show
  if (!keywords || (!content && !isStreaming && !error)) return null;

  const { body, suggestions } = parseSuggestions(content);

  const formatPrice = pence => `£${(pence / 100).toFixed(0)}`;

  // Build a compact summary for the collapsed state
  const summaryText = body ? body.split('\n')[0].slice(0, 80) : '';
  const priceRange = priceStats
    ? `${formatPrice(priceStats.min)} - ${formatPrice(priceStats.max)}`
    : '';

  return (
    <div
      className={classNames(css.root, {
        [css.rootExpanded]: isExpanded,
      })}
    >
      {/* Collapsed: single-line summary */}
      <button
        type="button"
        className={css.summaryRow}
        onClick={() => isComplete && setIsExpanded(prev => !prev)}
        aria-expanded={isExpanded}
      >
        <span className={css.aiLabel}>AI</span>

        {/* Loading shimmer in collapsed state */}
        {isStreaming && !body && <span className={css.shimmerInline} />}

        {/* Summary text */}
        {body && !isExpanded && (
          <span className={css.summaryText}>
            {summaryText}
            {summaryText.length >= 80 ? '...' : ''}
          </span>
        )}

        {/* Inline price badge */}
        {priceRange && !isExpanded && <span className={css.priceBadge}>{priceRange}</span>}

        {/* Expand indicator */}
        {isComplete && <span className={css.expandIcon}>{isExpanded ? '▴' : '▾'}</span>}
      </button>

      {/* Expanded body */}
      <div className={classNames(css.expandable, { [css.expandableOpen]: isExpanded })}>
        <div className={css.expandableInner}>
          {/* AI response text */}
          {body && <p className={css.responseText}>{body}</p>}

          {/* Error state */}
          {error && !content && (
            <p className={css.errorText}>Unable to generate insights right now.</p>
          )}

          {/* Price stat chips */}
          {priceStats && isComplete && (
            <div className={css.priceChips}>
              <span className={css.priceChip}>
                <span className={css.priceChipLabel}>Avg</span> {formatPrice(priceStats.avg)}
              </span>
              <span className={css.priceChip}>
                <span className={css.priceChipLabel}>Low</span> {formatPrice(priceStats.min)}
              </span>
              <span className={css.priceChip}>
                <span className={css.priceChipLabel}>High</span> {formatPrice(priceStats.max)}
              </span>
            </div>
          )}

          {/* Follow-up suggestion pills */}
          {suggestions.length > 0 && isComplete && (
            <div className={css.suggestions}>
              {suggestions.map((s, i) => (
                <a
                  key={i}
                  href={`/s?keywords=${encodeURIComponent(s)}`}
                  className={css.suggestionPill}
                >
                  {s}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiIntentCard;
